import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { loginService } from './services/loginService';

// Templates
import LoginTemplate from './components/templates/LoginTemplate';
import MembroTemplate from './components/templates/MembroTemplate';
import PortalFullTemplate from './components/templates/PortalFullTemplate';

// Pages
import Login from './pages/Login';
import Cadastro from './pages/Cadastro';
import CompletarPerfil from './pages/CompletarPerfil';
import ConfirmacaoToken from './pages/ConfirmacaoToken';
import EsqueciSenha from './pages/EsqueciSenha';
import RedefinirSenha from './pages/RedefinirSenha';
import CadastroPendencia from './pages/CadastroPendencia';
import Identificacao from './pages/Identificacao';
import Home from './pages/Home';
import Membro from './pages/Membro';
import Licao from './pages/Licao';
import Declaracao from './pages/Declaracao';
import Carteirinha from './pages/Carteirinha';
import Rank from './pages/Rank';
import Sejaum from './pages/Sejaum';
import SocialCallback from './pages/SocialCallback';

// Components
import PwaUpdateModal from './components/PwaUpdateModal';

const AppRoutes: React.FC = () => {
  const { isLoggedIn, checkAuth } = useAuth();
  const [showPwaUpdate, setShowPwaUpdate] = useState<boolean>(false);

  // Check if user is logged in on app start
  useQuery('activeUser', loginService.activeUser, {
    enabled: isLoggedIn,
    onSuccess: (data) => {
      if (data) {
        checkAuth();
      }
    },
    onError: () => {
      // Handle error silently
    }
  });

  useEffect(() => {
    const updateOnlineStatus = (): void => {
      if (!navigator.onLine) {
        toast.error('Você está sem conexão, por favor verifique.', {
          position: "bottom-center",
          autoClose: 15000,
        });
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // PWA update check
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setShowPwaUpdate(true);
      });
    }

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  return (
    <>
      <Routes>
        {/* Login routes */}
        <Route path="/inicio" element={<LoginTemplate />}>
          <Route path="login" element={<Login />} />
          <Route path="cadastro" element={<Cadastro />} />
          <Route path="completar-perfil" element={<CompletarPerfil />} />
          <Route path="confirmacao-token" element={<ConfirmacaoToken />} />
          <Route path="esqueci-senha" element={<EsqueciSenha />} />
          <Route path="redefinir-senha" element={<RedefinirSenha />} />
          <Route path="cadastrado-pendencia" element={<CadastroPendencia />} />
        </Route>

        {/* Rota para reset de senha (link do email) */}
        <Route path="/reset-password" element={<RedefinirSenha />} />

        {/* Social Auth Callback */}
        <Route path="/auth/social/callback" element={<SocialCallback />} />
        <Route path="/auth/social/google/callback" element={<SocialCallback />} />
        <Route path="/auth/callback" element={<SocialCallback />} />

        {/* Identificação */}
        <Route path="/identificacao/*" element={<Identificacao />} />

        {/* Membro routes */}
        <Route path="/membro" element={<PortalFullTemplate />}>
          <Route index element={<Membro />} />
          <Route path="home" element={<Home />} />
        </Route>

        <Route path="/licao" element={<MembroTemplate />}>
          <Route index element={<Licao />} />
        </Route>

        <Route path="/declaracao" element={<MembroTemplate />}>
          <Route index element={<Declaracao />} />
        </Route>

        <Route path="/carteirinha" element={<MembroTemplate />}>
          <Route index element={<Carteirinha />} />
        </Route>

        <Route path="/rank" element={<MembroTemplate />}>
          <Route index element={<Rank />} />
        </Route>

        <Route path="/sejaum/*" element={<Sejaum />} />

        {/* Home route */}
        <Route path="/home" element={<Home />} />

        {/* Default redirects */}
        <Route path="/" element={<Navigate to="/inicio/login" replace />} />
        <Route path="/bneinoach" element={<Navigate to="/inicio/login" replace />} />
        <Route path="*" element={<Navigate to="/inicio/login" replace />} />
      </Routes>

      {/* PWA Update Modal */}
      {showPwaUpdate && (
        <PwaUpdateModal 
          open={showPwaUpdate} 
          onClose={() => setShowPwaUpdate(false)} 
        />
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App; 